/**
 * Platform-agnostic runner for warming a lineup's artist photos.
 *
 * Web and mobile both want the same behaviour — dedupe the lineup names,
 * resolve each one's photo URLs, and do it a few at a time instead of firing
 * the whole lineup at the edge function at once — but they download image
 * bytes with different primitives (`new Image()` vs `Image.prefetch`), and
 * they want to download a different amount of the lineup. The policy lives
 * here; each app injects its own I/O and says how much to download.
 */

/** How many artists to warm at once — fast, without hammering the edge function. */
export const ARTIST_PREFETCH_CONCURRENCY = 6;

/** The minimum an entry needs before it's worth a Spotify lookup. */
const MIN_ARTIST_NAME_LENGTH = 2;

export interface WarmableArtistImage {
  url: string;
}

export interface ArtistImagePrefetchIO {
  /** Resolve (and cache) the photos for one lineup entry. */
  resolve: (name: string) => Promise<WarmableArtistImage[]>;
  /** Pull the image bytes into the platform's image cache. */
  warmBytes: (url: string) => Promise<unknown>;
}

export interface ArtistImagePrefetchOptions {
  /**
   * Which entries' image bytes to download, on top of resolving their URLs.
   *
   * Defaults to none: resolving is cheap and benefits every artist, while
   * downloading is megabytes and only pays off for photos someone is actually
   * about to open. A caller that wants the whole lineup on disk (the crew app,
   * for offline use) passes the whole lineup.
   */
  download?: (string | null | undefined)[];
  concurrency?: number;
  /** Checked between artists so an unmounted screen stops its own warm. */
  isCancelled?: () => boolean;
}

/**
 * Trim, drop the unusable entries and dedupe — the set of names actually worth
 * a lookup. Case is preserved: the edge function normalises for matching, and
 * the original casing is what a fallback would show.
 */
export function normalizeArtistNames(names: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const raw of names ?? []) {
    const name = (raw ?? "").trim();
    if (name.length < MIN_ARTIST_NAME_LENGTH) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(name);
  }

  return result;
}

/**
 * A dependency value that changes exactly when the set of names does.
 *
 * JSON, not a joined string: artist names are producer-typed free text, so any
 * separator character can occur inside one. `["Alpha|Bravo"]` and
 * `["Alpha", "Bravo"]` must not collide, or splitting a B2B entry would leave
 * both halves un-warmed forever.
 */
export function artistNamesKey(names: string[]): string {
  return JSON.stringify(names);
}

/** Run `task` over `items`, at most `concurrency` at a time. */
async function runPooled<T>(
  items: T[],
  concurrency: number,
  task: (item: T) => Promise<void>,
  isCancelled: () => boolean,
): Promise<void> {
  const limit = Math.max(1, concurrency);
  let next = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      if (isCancelled()) return;
      await task(items[next++]);
    }
  });

  await Promise.all(workers);
}

/**
 * Warm a lineup's artist photos.
 *
 * Two phases, deliberately not interleaved: every name's URL is resolved
 * first, and only then are the requested photos downloaded. Downloading inside
 * the same lane would let one slow photo delay the URL lookups behind it — and
 * the URL lookup is the round-trip this whole feature exists to remove.
 *
 * Best-effort throughout: a failing name (no Spotify match, transient
 * edge-function error, image that won't load) is swallowed so it never blocks
 * the rest of the lineup.
 */
export async function runArtistImagePrefetch(
  names: (string | null | undefined)[],
  io: ArtistImagePrefetchIO,
  options: ArtistImagePrefetchOptions = {},
): Promise<void> {
  const {
    download = [],
    concurrency = ARTIST_PREFETCH_CONCURRENCY,
    isCancelled = () => false,
  } = options;

  const unique = normalizeArtistNames(names);
  const resolved = new Map<string, WarmableArtistImage[]>();

  await runPooled(
    unique,
    concurrency,
    async (name) => {
      try {
        resolved.set(name.toLowerCase(), await io.resolve(name));
      } catch {
        // best-effort — keep going for the remaining artists
      }
    },
    isCancelled,
  );

  if (isCancelled()) return;

  const urls = normalizeArtistNames(download).flatMap(
    (name) => resolved.get(name.toLowerCase())?.map((image) => image.url) ?? [],
  );
  if (urls.length === 0) return;

  await runPooled(
    Array.from(new Set(urls)),
    concurrency,
    async (url) => {
      try {
        await io.warmBytes(url);
      } catch {
        // best-effort — one photo that won't load must not stop the rest
      }
    },
    isCancelled,
  );
}
